<template>
  <div id="settings">
    <div class="loading-pane" :class="{ active: busy }">
      <div class="spin" />
    </div>

    <label class="option">
      <strong>Subscriber Benefits</strong>
      <br />
      <select v-model="settings.subscriberAllowance">
        <option value="off">Off</option>
        <option value="normal">5/10/25 &ndash; Fair</option>
        <option value="high">5/11/30 &ndash; Bonus</option>
        <option value="custom" v-if="isPremium">Custom</option>
      </select>
      <button
        v-if="settings.subscriberAllowance !== 'custom'"
        :disabled="busy"
        @click="updateSubBenefits()">Save</button>
      <br />
      <small>
        Allow your channel subscribers to receive a monthly allowance of points.
        Choose between a fair 1:1 ratio, or incentivize subscription tier
        upgrades with a bonus model.
      </small>
    </label>

    <form
      enctype="multipart/form-data"
      v-if="isPremium && settings.subscriberAllowance === 'custom'">
      <table class="custom-benefits-config">
        <thead>
          <th></th>
          <th>
            Monthly Points<br />
            <small>How many points subscribers get each month</small>
          </th>
          <th>
            Submission Fee<br />
            <small>Extra "fee" to pay on top of every submission</small>
          </th>
          <th>
            Stockpile Limit<br />
            <small>Maximum amount of <em>subscription</em> points that can be held at one time</small>
          </th>
          <th>
            Image<br />
            <small>Badge image to show on profile page</small>
          </th>
        </thead>
        <tbody>
          <tr
            v-for="benefit of customBenefits"
            :key="benefit.tier">
            <th>Tier {{ benefit.tier }}</th>
            <td>
              <input type="number" v-model.number="benefit.points" />
            </td>
            <td>
              <input type="number" v-model.number="benefit.fee" />
            </td>
            <td>
              <input type="number" v-model.number="benefit.cap" />
            </td>
            <td>
              <img v-if="benefit.image" :src="getBenefitBadgeUrl(benefit)" width="50" />
              <label>
                <input
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="benefit.image = $event.target.files[0]" />
                <button style="pointer-events:none">Upload</button>
              </label>
              <button @click.prevent="benefit.image = null" :disabled="benefit.image === null">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
      <button @click.prevent="setCustomBenefits()">Set Custom Benefits</button>
    </form>

    <hr />

    <form enctype="multipart/form-data" v-if="isPremium">
      <strong>Banner</strong>
      <br />
      <img v-if="bannerImage" :src="bannerImage" width="300" />
      <br />
      <input
        type="file"
        accept="image/*"
        @change="prepareBannerUpload($event.target.files)" />
      <button :disabled="busy || !newBanner" @click.prevent="uploadBanner()">Upload</button>
      <button :disabled="busy || !bannerImage" @click.prevent="removeBanner()">Remove</button>
      <div v-if="bannerError" v-text="bannerError" />
    </form>

    <!--
    <div class="sub-distro-chart">
      <div class="column tiers">
        <div class="x" />
        <div>Tier 1</div>
        <div>Tier 2</div>
        <div>Tier 3</div>
      </div>
      <div
        v-for="model of subDistributionModels"
        :key="model.key"
        @click="settings.subscriberAllowance = model.key"
        :class="{ chosen: settings.subscriberAllowance === model.key }"
        class="column">
        <div class="head" v-text="model.name" />
        <div :class="{ n: model.tiers[0] === 0 }" v-text="model.tiers[0]" />
        <div :class="{ n: model.tiers[1] === 0 }" v-text="model.tiers[1]" />
        <div :class="{ n: model.tiers[2] === 0 }" v-text="model.tiers[2]" />
      </div>
    </div>
    <hr />
    <table class="sub-points-chart">
      <colgroup>
        <col />
        <col
          v-for="model of subDistributionModels"
          :key="model.key"
          class="tier-col"
          :class="{ chosen: settings.subscriberAllowance === model.key }" />
      </colgroup>
      <thead>
        <tr>
          <td class="x"></td>
          <th
            scope="col"
            v-for="model of subDistributionModels"
            :key="model.key"
            class="model-head"
            @click="settings.subscriberAllowance = model.key"
            :class="{ chosen: settings.subscriberAllowance === model.key }">
            <label>
              <input
                v-model="settings.subscriberAllowance"
                type="radio"
                :value="model.key" />
              {{ model.name }}
            </label>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="tier of [0, 1, 2]"
          :key="tier">
          <th scope="row">Tier {{ tier + 1 }}</th>
          <td
            v-for="model of subDistributionModels"
            :key="model.key"
            v-text="model.tiers[tier]"
            :class="{ n: model.tiers[tier] === 0, p: model.key == 'high' && tier > 0, chosen: settings.subscriberAllowance === model.key }" />
        </tr>
      </tbody>
    </table>
    <table class="sub-points-chart">
      <colgroup>
        <col />
        <col :class="{ chosen: settings.subscriberAllowance === 'off' }" />
        <col :class="{ chosen: settings.subscriberAllowance === 'normal' }" />
        <col :class="{ chosen: settings.subscriberAllowance === 'high' }" />
      </colgroup>
      <thead>
        <tr>
          <td class="x"></td>
          <th scope="col" width="100">Off</th>
          <th scope="col" width="100">Fair</th>
          <th scope="col" width="100">Incentivized</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">Tier 1</th>
          <td class="n">0</td>
          <td>5</td>
          <td>5</td>
        </tr>
        <tr>
          <th scope="row">Tier 2</th>
          <td class="n">0</td>
          <td>10</td>
          <td class="p">11</td>
        </tr>
        <tr>
          <th scope="row">Tier 3</th>
          <td class="n">0</td>
          <td>25</td>
          <td class="p">30</td>
        </tr>
      </tbody>
    </table> -->
    <!-- <button @click="activateChannel()">Save and Activate</button> -->
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ApiHelper from '@/mixins/ApiHelper'
import config from '../config'

export default {
  name: 'Settings',
  mixins: [ApiHelper],

  data () {
    return {
      storageUrl: `https://${config.firestore.projectId}.appspot.com.storage.googleapis.com`,
      settings: {
        subscriberBenefits: false,
        subscriberAllowance: "off"
      },
      subDistributionModels: [
        { key: 'off',    name: 'Off',          tiers: [0, 0, 0] },
        // { key: 'low',    name: 'Cheap',        tiers: [2, 4, 10] },
        { key: 'normal', name: 'Fair',         tiers: [5, 10, 25] },
        { key: 'high',   name: 'Incentivized', tiers: [5, 11, 30] }
      ],
      customBenefits: [],

      busy: false,
      bannerError: null,
      newBanner: null
    }
  },

  computed: {
    ...mapState(['channel']),
    ...mapGetters(['isPremium']),
    subDistroOptions () {
      return this.subDistributionModels
        .map((m) => ({ value: m.key, label: m.name}))
    },
    subDistroPoints (key, idx) {
      const model = this.subDistributionModels.find((m) => m.key === key)
      if (!model) {
        return 0
      }
      return model.tiers[idx] || 0
    },
    bannerImage () {
      const channel = this.channel
      const img = channel && channel.settings && channel.settings.banner
      if (!img) {
        return false
      }
      return `${this.storageUrl}/${img}`
    }
  },

  methods: {
    async updateSubBenefits () {
      const preset = this.settings.subscriberAllowance
      this.busy = true
      await this
        .apiRequest(`channel/subscriber-benefits/${preset}`, {
          method: 'post'
        })
        .catch((err) => console.error(err))
      this.busy = false
    },

    prepareBannerUpload (files) {
      console.log('files', files)
      // this.newBanner = files[0]

      if (!files.length) {
        this.newBanner = null
        return
      }

      const formData = new FormData()

      Array
        .from(Array(files.length).keys())
        .map((x) => formData.append('banner', files[x], files[x].name))

      this.newBanner = formData
    },
    async uploadBanner () {
      this.bannerError = null
      this.busy = true
      await this
        .apiRequest('channel/banner', {
          method: 'post',
          data: this.newBanner
        })
        .catch((err) => this.bannerError = err)
      this.busy = false
    },
    async removeBanner () {
      this.bannerError = null
      this.busy = true
      await this.apiRequest('channel/banner', { method: 'delete' })
        .catch((err) => this.bannerError = err.toString())
      this.busy = false
    },
    async setCustomBenefits () {
      const benefits = this.customBenefits
      const data = new FormData()
      data.append('benefits', JSON.stringify(benefits))
      for (const benefit of benefits) {
        console.log(benefit.image)
        if (benefit.image && benefit.image instanceof File) {
          data.append(`badge-${benefit.tier}`, benefit.image, benefit.image.name)
        }
      }
      await this.apiRequest('channel/subscriber-benefits', {
        method: 'post',
        data
      })
      .catch((err) => console.error(err))
    },
    getBenefitBadgeUrl (benefit) {
      if (benefit.image instanceof File) {
        return URL.createObjectURL(benefit.image)
      }
      return `${this.storageUrl}/${benefit.image}`
    }
  },
  created () {
    this.customBenefits = this.channel.settings.subscriberBenefits
      .slice(0)
      .map((b) => ({ ...b }))
    try {
      const preset = this.channel.settings.subscriberBenefitsPreset || 'normal'
      this.settings.subscriberAllowance = preset
    } catch (err) {
      console.warn(err)
    }
  }
}
</script>

<style scoped>
.option small {
  display: block;
}

.sub-points-chart {
  border-spacing: 0;
  border-collapse: collapse;
}
.sub-points-chart th {
  background: #eee;
}
th,
td {
  text-align: center;
  padding: 0.5em;
  border: 1px solid #ccc;
}
td.x {
  border: 0 none;
}
td.n {
  color: #bbb;
}
td.p {
  color: #090;
}
.sub-points-chart small {
  font-size: 0.6em;
  display: block;
}
.sub-points-chart .chosen {
  background-color: #efe;
}

.sub-points-chart .model-head {
  width: 150px;
  cursor: pointer;
}
/* .sub-points-chart input[type="radio"] {
  display: none;
} */
.sub-points-chart td.chosen {
  background-color: #efe;
  font-weight: 600;
}

.sub-distro-chart {
  display: flex;

}
.sub-distro-chart .column {
  flex-basis: 120px;
  display: grid;
  grid-template-rows: repeat(4, 1fr);
  text-align: center;
  background-color: #fff;
  transition: transform 75ms ease-out;
}
.sub-distro-chart .column + .column {
  border-left: 1px solid #ccc;
  border-top: 1px solid #ccc;
}
.sub-distro-chart .column > div {
  padding: 0.5em;
  border-bottom: 1px solid #ccc;
  border-right: 1px solid #ccc;
}
.sub-distro-chart .column.tiers .x {
  border: 0 none;
}
.sub-distro-chart .column.tiers .x ~ div {
  background-color: #eee;
  text-align: right;
  border: 1px solid #999;
  border-top: 0 none;
}
.sub-distro-chart .column.tiers .x + div {
  border-top-left-radius: 4px;
  border-top: 1px solid #999;
}
.sub-distro-chart .column.tiers > div:last-of-type {
  border-bottom-left-radius: 4px;
}
.sub-distro-chart .column.chosen {
  transform: scale(1.1);
  box-shadow: 0 0 16px rgba(153, 205, 50, 0.5);
  border-radius: 4px;
  background-color: rgb(240, 250, 223);
}
.sub-distro-chart .column.chosen .head {
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
}
.sub-distro-chart .column.chosen > div:last-of-type {
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
}
.sub-distro-chart .head {
  background-color: #eee;
  font-weight: 600;
}

.custom-benefits-config {
  border-spacing: 0;
  border-collapse: collapse;
}
.custom-benefits-config thead small {
  font-size: 0.7em;
  font-weight: 200;
  color: gray;
}


.loading-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background-color: white;
  opacity: 0;
  transition: opacity 100ms linear;
  transition-delay: 0s;
}
.loading-pane.post-note {
  transition-delay: 3s;
}
.loading-pane.active {
  pointer-events: all;
  opacity: 0.9;
  transition-delay: 0s;
}
.loading-pane .message {
  margin-top: 2em;
  font-size: larger;
}
.loading-pane .spin {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 5px solid #ccc;
  border-top-color: dodgerblue;
  animation: spin 2s infinite linear;
}
@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
